import http from '@core/api/http';

import { wait } from '@utils/async';
import {
    findRequests,
    addRequest,
    rejectRequest,
    completeRequest,
    setRequestPromise,
    getAbortSignal,
    isAborted,
    cleanRequestData,
    attachHeaders
} from '@utils/api';

import clock from '@plugins/dayjs';

import envConfig from '@/config';

/**
 * Send a GET request.
 *
 * @param {String} url
 * @param {Object} params
 * @returns {Object}
 */
export async function get(url, params = {}, config = {}) {
    return await request({
        method: 'GET',
        url,
        params,
        ...config
    });
}

/**
 * Send a POST request.
 *
 * @param {String} url
 * @param {Object} data
 * @returns {Object}
 */
export async function post(url, data = {}, config = {}) {
    return await request({
        method: 'POST',
        url,
        data: cleanRequestData(data),
        ...config
    });
}

/**
 * Send a PUT request.
 *
 * @param {String} url
 * @param {Object} data
 * @returns {Object}
 */
export async function put(url, data = {}, config = {}) {
    return await request({
        method: 'PUT',
        url,
        data: cleanRequestData(data),
        ...config
    });
}

/**
 * Send a PATCH request.
 *
 * @param {String} url
 * @param {Object} data
 * @returns {Object}
 */
export async function patch(url, data = {}, config = {}) {
    return await request({
        method: 'PATCH',
        url,
        data: cleanRequestData(data),
        ...config
    });
}

/**
 * Send a DELETE request.
 *
 * @param {String} url
 * @returns {Object}
 */
export async function remove(url, config = {}) {
    return await request({
        method: 'DELETE',
        url,
        ...config
    });
}

/**
 * Send a HEAD request.
 *
 * @param {String} url
 * @returns {Object}
 */
export async function head(url, config = {}) {
    return await request({
        method: 'HEAD',
        url,
        ...config
    });
}

/**
 * Send a OPTIONS request.
 *
 * @param {String} url
 * @returns {Object}
 */
export async function options(url, config = {}) {
    return await request({
        method: 'OPTIONS',
        url,
        ...config
    });
}

/**
 * Send a request.
 *
 * @param {Object} config
 * @returns {Object}
 */
export async function request(config = {}) {
    let response = null;

    // Add the scope (Organisation ID of the current user) to the request headers.
    config.headers = attachHeaders(config.headers);

    if (!config.persistent && config.method === 'GET') {
        const pending = findRequests(config).filter(request => request.status === 'pending');

        if (pending.length > 0) {
            await wait(100);
            response = await pending[0].promise;
        }

        if (!response) {
            const completed = findRequests(config).filter(request => {
                return (
                    request.status === 'completed' &&
                    clock().diff(clock(request.response_at), 'milliseconds') < envConfig.API_LIFETIME
                );
            });

            if (completed.length > 0) {
                response = completed[0].response;
            }
        }
    }

    if (!response) {
        const requestId = await addRequest({
            url: config.url,
            method: config.method,
            params: config.params || {},
            headers: config.headers || {},
            persistent: config.persistent,
            abortGroup: config.abortGroup || null
        });

        const signal = getAbortSignal(requestId);
        const promise = http.request({ ...config, signal });

        setRequestPromise(requestId, promise);

        response = await promise.catch(async error => {
            if (isAborted(requestId)) {
                const abortedError = new Error(`Request ${config.method} ${config.url} was aborted.`);
                abortedError.isAborted = true;
                throw abortedError;
            }

            rejectRequest(requestId, error);

            if (error && error.response && error.response.status) {
                error.code = error.response.status;
            } else if (error && error.request && error.request.status) {
                error.code = error.request.status;
            }

            if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
                error.response.data = await error.response.data.text();
                error.response.data = JSON.parse(error.response.data);
            }

            throw error;
        });

        completeRequest(requestId, response);
    }

    if (config.returnFullResponse) {
        return response;
    } else {
        return response.data;
    }
}
